/**
 * Owner: Masa Bayu
 * File: batch05-evidence.ts
 * Date:Time: 2026-09-20 00:25
 * Description: Batch 5 evidence packs — Indic shaping · READY · not Supported.
 */
import { QX_BATCH_05_EVIDENCE_AREAS, QX_BATCH_05_FAMILY_IDS, QX_BATCH_05_MODEL_VERSION, QX_GL_CERT_GLOBAL_BATCH_05_ID, } from './batch05-codes';
import { fixturesForBatch05Family, listBatch05FixtureIds, QX_BATCH_05_SAMPLE_LANGUAGES, QX_BATCH_05_TEXT_SAMPLES, } from './batch05-fixtures';
import { createQxAccessibilityLanguageSemantics } from './a11y-semantics';
import { allEvidenceTrue, evaluateEvidencePath, } from './certification-evidence';
import { createQxFamilyCertificationRow, supportClaimForRow, } from './certification-row';
import { getCoverageScriptDirectionRow } from './coverage-script-matrix';
import { resolveDirectionHint } from './direction-resolve';
import { QxLanguageError } from './errors';
import { createQxLanguageIdentity } from './identity';
import { assertQxpLanguageMetadataFromQxwOnly, createQxLanguageMetadataBinding, preservePublicationLanguageMetadata, } from './metadata-placement';
import { applyOverrideWithoutChangingContentId, createQxMultilingualContentModel, } from './multilingual-content';
import { roundTripQxMultilingualContentModel, serializeQxMultilingualContentModel, } from './multilingual-serialize';
import { parseQxPublicationLanguageMetadata, serializeQxPublicationLanguageMetadata, } from './metadata-serialize';
import { createQxPublicationLanguageMetadata } from './publication-metadata';
import { resolveLanguageScriptRelation } from './script-relation';
import { createQxTextProcessingProfile, prepareSearchText, } from './search-prep';
import { selectSegmentStrategy } from './text-segment';
import { normalizeTextForSearch } from './text-normalize';
import { resolveTypographyForScript } from './typography-resolve';
import { resolveWritingModeHint } from './writing-mode-resolve';
const FAMILY_CFG = Object.freeze({
    devanagari: Object.freeze({
        scriptId: 'Deva',
        primary: 'hi',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.hi,
        contentId: 'doc-deva-batch05-01',
        analyzer: 'devanagari_script',
    }),
    bengali: Object.freeze({
        scriptId: 'Beng',
        primary: 'bn',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.bn,
        contentId: 'doc-beng-batch05-01',
        analyzer: 'bengali_script',
    }),
    gurmukhi: Object.freeze({
        scriptId: 'Guru',
        primary: 'pa',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.pa,
        contentId: 'doc-guru-batch05-01',
        analyzer: 'gurmukhi_script',
    }),
    gujarati: Object.freeze({
        scriptId: 'Gujr',
        primary: 'gu',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.gu,
        contentId: 'doc-gujr-batch05-01',
        analyzer: 'gujarati_script',
    }),
    odia: Object.freeze({
        scriptId: 'Orya',
        primary: 'or',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.or,
        contentId: 'doc-orya-batch05-01',
        analyzer: 'odia_script',
    }),
    tamil: Object.freeze({
        scriptId: 'Taml',
        primary: 'ta',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.ta,
        contentId: 'doc-taml-batch05-01',
        analyzer: 'tamil_script',
    }),
    telugu: Object.freeze({
        scriptId: 'Telu',
        primary: 'te',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.te,
        contentId: 'doc-telu-batch05-01',
        analyzer: 'telugu_script',
    }),
    kannada: Object.freeze({
        scriptId: 'Knda',
        primary: 'kn',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.kn,
        contentId: 'doc-knda-batch05-01',
        analyzer: 'kannada_script',
    }),
    malayalam: Object.freeze({
        scriptId: 'Mlym',
        primary: 'ml',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.ml,
        contentId: 'doc-mlym-batch05-01',
        analyzer: 'malayalam_script',
    }),
    sinhala: Object.freeze({
        scriptId: 'Sinh',
        primary: 'si',
        sampleText: QX_BATCH_05_TEXT_SAMPLES.si,
        contentId: 'doc-sinh-batch05-01',
        analyzer: 'sinhala_script',
    }),
});
function allAreasTrue(areas) {
    for (const key of QX_BATCH_05_EVIDENCE_AREAS) {
        if (!areas[key])
            return false;
    }
    return true;
}
function whitespaceOnlyRejected(languageId) {
    try {
        selectSegmentStrategy(languageId, 'whitespace');
        return false;
    }
    catch {
        return true;
    }
}
/**
 * Build one Batch 5 Indic family evidence pack.
 * Proves shaping · script_aware · combining/conjunct-safe path Author→Reader.
 */
export function buildBatch05FamilyEvidenceBundle(familyId) {
    const cfg = FAMILY_CFG[familyId];
    const samples = QX_BATCH_05_SAMPLE_LANGUAGES[familyId];
    const areas = {};
    const fixtures = fixturesForBatch05Family(familyId);
    const matrixRow = getCoverageScriptDirectionRow(familyId);
    const primary = createQxLanguageIdentity({
        languageId: cfg.primary,
        plane: 'publication',
    });
    areas.identity =
        primary.languageId === cfg.primary && samples.includes(cfg.primary);
    const scriptRel = resolveLanguageScriptRelation(cfg.primary);
    areas.script =
        scriptRel.resolvedScriptId === cfg.scriptId &&
            matrixRow.primaryScriptIds.includes(cfg.scriptId) &&
            matrixRow.complexShaping === true;
    const dir = resolveDirectionHint(cfg.primary);
    const dirOverride = resolveDirectionHint(cfg.primary, 'ltr');
    areas.direction =
        dir.hintedDirection === 'ltr' && dirOverride.override === 'ltr';
    const content = createQxMultilingualContentModel({
        contentId: cfg.contentId,
        primaryLanguageId: cfg.primary,
        contentLanguages: [...samples],
        overrides: [],
    });
    areas.content = content.primaryLanguageId === cfg.primary;
    const pubMeta = createQxPublicationLanguageMetadata({
        primaryLanguageId: cfg.primary,
        contentLanguages: [...samples],
    });
    const qxwBinding = createQxLanguageMetadataBinding({
        placement: 'qxw_document_root',
        primaryLanguageId: pubMeta.primaryLanguageId,
        defaultScriptId: pubMeta.defaultScriptId,
        defaultDirection: pubMeta.defaultDirection,
        defaultWritingMode: pubMeta.defaultWritingMode,
        contentLanguages: pubMeta.contentLanguages,
    });
    const preserved = preservePublicationLanguageMetadata(qxwBinding.metadata);
    areas.qxw =
        preserved.primaryLanguageId === cfg.primary &&
            preserved.defaultScriptId === cfg.scriptId &&
            preserved.defaultDirection === 'ltr';
    let qxpOk = false;
    try {
        assertQxpLanguageMetadataFromQxwOnly(true);
        const qxp = createQxLanguageMetadataBinding({
            placement: 'qxp_artifact',
            primaryLanguageId: cfg.primary,
            contentLanguages: [...samples],
        });
        qxpOk =
            qxp.preserveThroughPublish === true &&
                qxp.metadata.defaultDirection === 'ltr';
    }
    catch {
        qxpOk = false;
    }
    areas.qxp = qxpOk;
    const typo = resolveTypographyForScript({
        role: 'body',
        languageId: cfg.primary,
    });
    const heading = resolveTypographyForScript({
        role: 'heading',
        languageId: cfg.primary,
    });
    areas.reader =
        typo.scriptId === cfg.scriptId &&
            heading.scriptId === cfg.scriptId &&
            typo.shapingRequired === true &&
            typo.fallbackChain.length > 0;
    const profile = createQxTextProcessingProfile({ languageId: cfg.primary });
    const prep = prepareSearchText(profile, cfg.sampleText);
    const nfc = normalizeTextForSearch(QX_BATCH_05_TEXT_SAMPLES.composed, 'NFC');
    areas.search =
        profile.analyzerFamily === cfg.analyzer &&
            profile.segmentStrategy === 'script_aware' &&
            prep.segmentation.strategy === 'script_aware' &&
            prep.segmentation.engineRequired === true &&
            whitespaceOnlyRejected(cfg.primary) &&
            cfg.sampleText.length > 0 &&
            nfc.normalized === 'café' &&
            nfc.original === QX_BATCH_05_TEXT_SAMPLES.composed;
    const wm = resolveWritingModeHint(cfg.primary);
    areas.navigation =
        wm.hintedWritingMode === 'horizontal-tb' && heading.role === 'heading';
    areas.annotation = nfc.original !== nfc.normalized && nfc.original.length > 0;
    const a11y = createQxAccessibilityLanguageSemantics({
        contentLanguageId: cfg.primary,
        uiAnnouncementLanguageId: 'en',
        spans: [],
    });
    areas.accessibility =
        a11y.contentLangTag === cfg.primary &&
            a11y.direction === 'ltr' &&
            a11y.readingOrderPreserved === true;
    const metaRt = parseQxPublicationLanguageMetadata(serializeQxPublicationLanguageMetadata(pubMeta));
    const contentRt = roundTripQxMultilingualContentModel(content);
    const json = serializeQxMultilingualContentModel(content);
    areas.serialization =
        metaRt.primaryLanguageId === cfg.primary &&
            metaRt.defaultDirection === 'ltr' &&
            contentRt.contentId === cfg.contentId &&
            typeof json.modelVersion === 'string';
    const next = applyOverrideWithoutChangingContentId(content, {
        scope: 'block',
        spanId: `note-${cfg.primary}`,
        languageId: cfg.primary,
    }, cfg.contentId);
    areas.regression =
        next.contentId === content.contentId && next.overrides.length === 1;
    const provenance = fixtures.map((f) => Object.freeze({ fixtureId: f.id, area: f.area }));
    const areaSet = new Set(QX_BATCH_05_EVIDENCE_AREAS);
    areas.provenance =
        provenance.length === QX_BATCH_05_EVIDENCE_AREAS.length &&
            provenance.every((p) => areaSet.has(p.area));
    const frozenAreas = Object.freeze({ ...areas });
    const pathEvidence = allEvidenceTrue();
    const pathEval = evaluateEvidencePath(pathEvidence);
    const areasPass = allAreasTrue(frozenAreas);
    const row = createQxFamilyCertificationRow({
        familyId,
        scriptIds: [cfg.scriptId],
        sampleLanguageIds: [...samples],
        directions: ['ltr'],
        level: 'experimental',
        evidence: pathEvidence,
        evidenceRefs: listBatch05FixtureIds(familyId),
        gaps: ['awaiting_owner_accept_for_certified', 'batch_open_not_supported'],
        ownerAcceptedAt: null,
    });
    const claimStatus = supportClaimForRow(row);
    if (claimStatus === 'supported') {
        throw new QxLanguageError('INVALID_INPUT', `${familyId} must not claim Supported before Owner Accept`);
    }
    const ready = areasPass && pathEval.status === 'pass';
    return Object.freeze({
        modelVersion: QX_BATCH_05_MODEL_VERSION,
        batchId: QX_GL_CERT_GLOBAL_BATCH_05_ID,
        familyId,
        scriptId: cfg.scriptId,
        areas: frozenAreas,
        pathEvidence,
        pathStatus: pathEval.status,
        fixtures: listBatch05FixtureIds(familyId),
        provenance: Object.freeze(provenance),
        row,
        supportedClaimAllowed: false,
        claimStatus,
        readyForOwnerAccept: ready,
        automatedTestsPass: ready,
        manualQaChecklistPackaged: true,
        a11ySpotCheckPass: areas.accessibility,
        notes: ready
            ? `${familyId} Batch 5 Indic evidence READY — not Supported`
            : `${familyId} Batch 5 evidence incomplete`,
    });
}
export function buildBatch05EvidenceReport() {
    const families = {};
    let ready = true;
    for (const id of QX_BATCH_05_FAMILY_IDS) {
        const bundle = buildBatch05FamilyEvidenceBundle(id);
        families[id] = bundle;
        if (!bundle.readyForOwnerAccept)
            ready = false;
    }
    return Object.freeze({
        batchId: QX_GL_CERT_GLOBAL_BATCH_05_ID,
        modelVersion: QX_BATCH_05_MODEL_VERSION,
        families: Object.freeze(families),
        readyForOwnerAccept: ready,
        batchOpenImpliesSupported: false,
        supportedFamilyIdsStill: Object.freeze([
            'latin-core',
            'arabic',
            'hebrew',
            'cyrillic',
            'greek',
            'chinese',
            'japanese',
            'korean',
            'thai',
            'lao',
            'khmer',
            'myanmar',
        ]),
    });
}
//# sourceMappingURL=batch05-evidence.js.map